<!-- FileUploadModal.vue -->
<template>
  <transition name="body modal-fade">
    <div class="file-upload-modal-overlay" @click.self="closeModal">
      <div class="file-upload-modal">
        <!-- Modal Header -->
        <div class="modal-header">
          <h3>📁 {{ $t('messenger.fileUploadModal.title') }}</h3>
          <button class="close-btn" @click="closeModal" :aria-label="$t('messenger.fileUploadModal.close')">
            &times;
          </button>
        </div>
        
        <!-- Modal Content -->
        <div class="modal-content">
          <!-- File Drop Zone -->
          <div 
            class="file-drop-zone"
            :class="{ 'drag-over': isDragging, 'has-file': selectedFile }"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
          >
            <!-- Upload Icon -->
            <div class="upload-icon">
              <div v-if="!selectedFile" class="upload-placeholder">
                <span class="icon">📤</span>
                <p class="instruction">{{ $t('messenger.fileUploadModal.drop_files') }}</p>
                <p class="sub-instruction">{{ $t('messenger.fileUploadModal.click_to_select') }}</p>
              </div>
              
              <!-- File Preview -->
              <div v-else class="file-preview">
                <div class="preview-icon" :class="fileType">
                  <span v-if="fileType === 'image'" class="image-preview">
                    <img :src="filePreviewUrl" :alt="selectedFile.name" @load="handleImageLoad" />
                  </span>
                  <span v-else class="file-icon">
                    {{ getFileIcon(selectedFile.type) }}
                  </span>
                </div>
                <div class="file-info">
                  <p class="file-name">{{ selectedFile.name }}</p>
                  <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
                  <p class="file-type">{{ getFileTypeName(selectedFile.type) }}</p>
                </div>
                <button class="remove-file-btn" @click.stop="removeFile" :title="$t('messenger.fileUploadModal.remove_file')">
                  &times;
                </button>
              </div>
            </div>
            
            <!-- Progress Bar (for upload) -->
            <div v-if="uploadProgress > 0" class="upload-progress">
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: `${uploadProgress}%` }"
                  :class="{ 'uploading': isUploading, 'success': uploadSuccess, 'error': uploadError }"
                ></div>
              </div>
              <div class="progress-info">
                <span>{{ uploadProgress }}%</span>
                <span v-if="isUploading">{{ $t('messenger.fileUploadModal.uploading') }}</span>
                <span v-else-if="uploadSuccess" class="success-text">✅ {{ $t('messenger.fileUploadModal.upload_success') }}</span>
                <span v-else-if="uploadError" class="error-text">❌ {{ $t('messenger.fileUploadModal.upload_error') }}</span>
              </div>
            </div>
          </div>
          
          <!-- File Input (hidden) -->
          <input
            ref="fileInput"
            type="file"
            @change="handleFileSelect"
            class="file-input-hidden"
            :accept="allowedFileTypes"
            multiple
          />
          
          <!-- File Details -->
          <div v-if="selectedFile" class="file-details">
            <div class="detail-item">
              <span class="detail-label">{{ $t('messenger.fileUploadModal.file_name') }}:</span>
              <span class="detail-value">{{ selectedFile.name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('messenger.fileUploadModal.file_size') }}:</span>
              <span class="detail-value">{{ formatFileSize(selectedFile.size) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('messenger.fileUploadModal.file_type_label') }}:</span>
              <span class="detail-value">{{ getFileTypeName(selectedFile.type) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('messenger.fileUploadModal.modified_date') }}:</span>
              <span class="detail-value">{{ formatDate(selectedFile.lastModified) }}</span>
            </div>
          </div>
          
          <!-- Caption Input -->
          <div class="caption-section">
            <label for="file-caption">{{ $t('messenger.fileUploadModal.caption_label') }}</label>
            <textarea
              id="file-caption"
              v-model="fileCaption"
              :placeholder="$t('messenger.fileUploadModal.caption_placeholder')"
              rows="2"
              maxlength="200"
              class="caption-input"
            ></textarea>
            <div class="char-counter">
              {{ fileCaption.length }}/200
            </div>
          </div>
          
          <!-- File Size Warning -->
          <div v-if="selectedFile && selectedFile.size > maxFileSize" class="size-warning">
            ⚠️ {{ $t('messenger.fileUploadModal.file_size_warning', { size: formatFileSize(maxFileSize) }) }}
          </div>
        </div>
        
        <!-- Modal Footer -->
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal" :disabled="isUploading">
            {{ $t('messenger.fileUploadModal.cancel') }}
          </button>
          <button 
            class="send-btn" 
            @click="sendFile"
            :disabled="!selectedFile || isUploading || selectedFile.size > maxFileSize"
            :class="{ 'loading': isUploading }"
          >
            <span v-if="isUploading" class="loading-spinner"></span>
            <span v-else>📤 {{ $t('messenger.fileUploadModal.send_file') }}</span>
          </button>
        </div>
        
        <!-- Upload Status -->
        <div v-if="uploadStatus" class="upload-status" :class="uploadStatus.type">
          <span class="status-icon">{{ uploadStatus.icon }}</span>
          <span class="status-text">{{ uploadStatus.message }}</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'

export default {
  name: 'FileUploadModal',
  props: {
    chatId: {
      type: String,
      required: true
    },
    senderId: {
      type: String,
      required: true
    }
  },
  emits: ['file-sent', 'close'],
  setup(props, { emit }) {
    // Refs
    const fileInput = ref(null)
    const selectedFile = ref(null)
    const filePreviewUrl = ref('')
    const isDragging = ref(false)
    const isUploading = ref(false)
    const uploadProgress = ref(0)
    const uploadSuccess = ref(false)
    const uploadError = ref(false)
    const fileCaption = ref('')
    const activeFilter = ref('all')
    const recentFiles = ref([])
    
    // Constants
    const maxFileSize = 10 * 1024 * 1024 // 10MB
    const allowedFileTypes = ref('*/*') // Will be set based on filter
    
    // File type filters
    const fileFilters = [
      { type: 'all', label: 'همه فایل‌ها', icon: '📁', accept: '*/*' },
      { type: 'image', label: 'تصاویر', icon: '🖼️', accept: 'image/*' },
      { type: 'document', label: 'اسناد', icon: '📄', accept: '.pdf,.doc,.docx,.txt' },
      { type: 'video', label: 'ویدیوها', icon: '🎬', accept: 'video/*' },
      { type: 'audio', label: 'صداها', icon: '🎵', accept: 'audio/*' }
    ]
    
    // Computed
    const fileType = computed(() => {
      if (!selectedFile.value) return 'unknown'
      const type = selectedFile.value.type
      if (type.startsWith('image/')) return 'image'
      if (type.startsWith('video/')) return 'video'
      if (type.startsWith('audio/')) return 'audio'
      if (type.includes('pdf') || type.includes('document')) return 'document'
      return 'other'
    })
    
    const uploadStatus = computed(() => {
      if (uploadSuccess.value) {
        return {
          type: 'success',
          icon: '✅',
          message: 'فایل با موفقیت ارسال شد!'
        }
      }
      if (uploadError.value) {
        return {
          type: 'error',
          icon: '❌',
          message: 'خطا در ارسال فایل'
        }
      }
      if (isUploading.value) {
        return {
          type: 'uploading',
          icon: '⏳',
          message: 'در حال آپلود...'
        }
      }
      return null
    })
    
    // Methods
    const triggerFileInput = () => {
      if (!isUploading.value) {
        fileInput.value.click()
      }
    }
    
    const handleFileSelect = (event) => {
      const files = event.target.files
      if (files.length > 0) {
        processFile(files[0])
      }
    }
    
    const handleDragOver = (event) => {
      event.preventDefault()
      isDragging.value = true
    }
    
    const handleDragLeave = () => {
      isDragging.value = false
    }
    
    const handleDrop = (event) => {
      event.preventDefault()
      isDragging.value = false
      
      const files = event.dataTransfer.files
      if (files.length > 0) {
        processFile(files[0])
      }
    }
    
    const processFile = (file) => {
      // Check file size
      if (file.size > maxFileSize) {
        alert(`فایل انتخابی بزرگتر از حد مجاز است (${formatFileSize(maxFileSize)})`)
        return
      }
      
      selectedFile.value = file
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          filePreviewUrl.value = e.target.result
        }
        reader.readAsDataURL(file)
      } else {
        filePreviewUrl.value = ''
      }
      
      // Add to recent files
      addToRecentFiles(file)
    }
    
    const handleImageLoad = () => {
      console.log('Image preview loaded')
    }
    
    const removeFile = () => {
      selectedFile.value = null
      filePreviewUrl.value = ''
      fileCaption.value = ''
      uploadProgress.value = 0
      uploadSuccess.value = false
      uploadError.value = false
    }
    
    const setActiveFilter = (filterType) => {
      activeFilter.value = filterType
      const filter = fileFilters.find(f => f.type === filterType)
      if (filter) {
        allowedFileTypes.value = filter.accept
      }
    }
    
    const getFileIcon = (fileType) => {
      if (fileType.startsWith('image/')) return '🖼️'
      if (fileType.includes('pdf')) return '📕'
      if (fileType.includes('document') || fileType.includes('word')) return '📄'
      if (fileType.startsWith('video/')) return '🎬'
      if (fileType.startsWith('audio/')) return '🎵'
      if (fileType.includes('zip') || fileType.includes('rar')) return '📦'
      if (fileType.includes('excel') || fileType.includes('sheet')) return '📊'
      return '📁'
    }
    
    const getFileTypeClass = (fileType) => {
      if (fileType.startsWith('image/')) return 'image'
      if (fileType.includes('pdf')) return 'pdf'
      if (fileType.includes('document') || fileType.includes('word')) return 'document'
      if (fileType.startsWith('video/')) return 'video'
      if (fileType.startsWith('audio/')) return 'audio'
      return 'other'
    }
    
    const getFileTypeName = (fileType) => {
      const typeMap = {
        'image/jpeg': 'تصویر JPEG',
        'image/png': 'تصویر PNG',
        'image/gif': 'تصویر GIF',
        'application/pdf': 'PDF',
        'application/msword': 'سند Word',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'سند Word',
        'video/mp4': 'ویدیو MP4',
        'audio/mpeg': 'فایل صوتی MP3',
        'text/plain': 'فایل متنی',
        'application/zip': 'فایل فشرده ZIP',
        'application/x-rar-compressed': 'فایل فشرده RAR'
      }
      return typeMap[fileType] || fileType || 'ناشناخته'
    }
    
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 بایت'
      const k = 1024
      const sizes = ['بایت', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
    
    const formatDate = (timestamp) => {
      const date = new Date(timestamp)
      return date.toLocaleDateString('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    const addToRecentFiles = (file) => {
      // Create file object
      const fileObj = {
        id: Date.now(),
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        file: file // Store reference to actual File object
      }
      
      // Add to beginning of array
      recentFiles.value.unshift(fileObj)
      
      // Keep only last 5 files
      if (recentFiles.value.length > 5) {
        recentFiles.value = recentFiles.value.slice(0, 5)
      }
      
      // Save to localStorage
      saveRecentFiles()
    }
    
    const selectRecentFile = (fileObj) => {
      selectedFile.value = fileObj.file
      
      // Create preview for images
      if (fileObj.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          filePreviewUrl.value = e.target.result
        }
        reader.readAsDataURL(fileObj.file)
      } else {
        filePreviewUrl.value = ''
      }
    }
    
    const saveRecentFiles = () => {
      try {
        // We can't store File objects in localStorage, so we store metadata only
        const recentFilesData = recentFiles.value.map(file => ({
          id: file.id,
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified
          // Note: We don't store the actual File object
        }))
        
        localStorage.setItem('chat_recent_files', JSON.stringify(recentFilesData))
      } catch (error) {
        console.error('Error saving recent files:', error)
      }
    }
    
    const loadRecentFiles = () => {
      try {
        const saved = localStorage.getItem('chat_recent_files')
        if (saved) {
          recentFiles.value = JSON.parse(saved)
        }
      } catch (error) {
        console.error('Error loading recent files:', error)
      }
    }
    
    const simulateUploadProgress = () => {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          uploadProgress.value += Math.random() * 10
          if (uploadProgress.value >= 100) {
            uploadProgress.value = 100
            clearInterval(interval)
            resolve()
          }
        }, 100)
      })
    }
    
    const sendFile = async () => {
      if (!selectedFile.value || isUploading.value) return
      
      isUploading.value = true
      uploadProgress.value = 0
      uploadSuccess.value = false
      uploadError.value = false
      
      try {
        // Simulate upload progress (in real app, use actual upload progress)
        await simulateUploadProgress()
        
        // Here you would call your actual file upload API
        // Example:
        // const formData = new FormData()
        // formData.append('file', selectedFile.value)
        // formData.append('chatId', props.chatId)
        // formData.append('senderId', props.senderId)
        // formData.append('caption', fileCaption.value)
        // 
        // const response = await messengerService.sendFile(
        //   props.chatId,
        //   props.senderId,
        //   selectedFile.value,
        //   'customer'
        // )
        
        // For now, simulate successful upload
        setTimeout(() => {
          uploadSuccess.value = true
          isUploading.value = false
          
          // Emit event to parent
          emit('file-sent', {
            file: selectedFile.value,
            caption: fileCaption.value,
            timestamp: new Date().toISOString()
          })
          
          // Close modal after success
          setTimeout(() => {
            closeModal()
          }, 1500)
        }, 500)
        
      } catch (error) {
        console.error('Error uploading file:', error)
        uploadError.value = true
        isUploading.value = false
        
        // Show error message
        alert('خطا در آپلود فایل. لطفاً دوباره تلاش کنید.')
      }
    }
    
    const closeModal = () => {
      if (!isUploading.value) {
        emit('close')
      }
    }
    
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && !isUploading.value) {
        closeModal()
      }
    }
    
    // Lifecycle
    onMounted(() => {
      loadRecentFiles()
      document.addEventListener('keydown', handleEscapeKey)
    })
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscapeKey)
      
      // Clean up object URLs
      if (filePreviewUrl.value) {
        URL.revokeObjectURL(filePreviewUrl.value)
      }
    })
    
    return {
      // Refs
      fileInput,
      selectedFile,
      filePreviewUrl,
      isDragging,
      isUploading,
      uploadProgress,
      uploadSuccess,
      uploadError,
      fileCaption,
      activeFilter,
      recentFiles,
      
      // Computed
      fileType,
      uploadStatus,
      allowedFileTypes,
      
      // Constants
      maxFileSize,
      fileFilters,
      
      // Methods
      triggerFileInput,
      handleFileSelect,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleImageLoad,
      removeFile,
      setActiveFilter,
      getFileIcon,
      getFileTypeClass,
      getFileTypeName,
      formatFileSize,
      formatDate,
      selectRecentFile,
      sendFile,
      closeModal
    }
  }
}
</script>

<style scoped>
.file-upload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--modal-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.file-upload-modal {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 10px 30px var(--shadow-2);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--card-border);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-content {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.file-drop-zone {
  border: 2px dashed var(--card-border);
  border-radius: 10px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  background: var(--bg);
}

.file-drop-zone:hover {
  border-color: var(--primary);
  background: var(--ring);
}

.file-drop-zone.drag-over {
  border-color: var(--green);
  background: rgba(16, 185, 129, 0.1);
  transform: scale(1.02);
}

.file-drop-zone.has-file {
  border-style: solid;
  border-color: var(--primary);
  padding: 20px;
}

.upload-placeholder .icon {
  font-size: 48px;
  margin-bottom: 10px;
  display: block;
}

.instruction {
  font-size: 16px;
  font-weight: 500;
  margin: 10px 0;
  color: var(--heading);
}

.sub-instruction {
  font-size: 14px;
  color: var(--subtext);
  margin: 5px 0;
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 15px;
  position: relative;
}

.preview-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  font-size: 24px;
  flex-shrink: 0;
  overflow: hidden;
}

.preview-icon.image {
  background: var(--card-bg);
}

.preview-icon.image .image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-info {
  flex: 1;
  text-align: right;
}

.file-name {
  font-weight: 500;
  margin: 0 0 5px 0;
  color: var(--heading);
  font-size: 14px;
  direction: rtl;
  text-align: right;
}

.file-size, .file-type {
  margin: 2px 0;
  color: var(--subtext);
  font-size: 12px;
}

.remove-file-btn {
  position: absolute;
  top: -10px;
  left: -10px;
  background: var(--orange-1);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.remove-file-btn:hover {
  transform: scale(1.1);
}

.upload-progress {
  margin-top: 15px;
}

.progress-bar {
  height: 6px;
  background: var(--card-border);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-fill {
  height: 100%;
  background: var(--green);
  transition: width 0.3s ease;
}

.progress-fill.uploading {
  background: linear-gradient(90deg, var(--gradient-1), var(--gradient-2));
  animation: pulse 1.5s ease-in-out infinite;
}

.progress-fill.success {
  background: var(--green);
}

.progress-fill.error {
  background: var(--orange-1);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--subtext);
}

.success-text {
  color: var(--green);
}

.error-text {
  color: var(--orange-1);
}

.file-input-hidden {
  display: none;
}

.file-type-filters {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 8px 12px;
  border: 1px solid var(--card-border);
  border-radius: 20px;
  background: var(--card-bg);
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--text);
}

.filter-btn:hover {
  border-color: var(--primary);
  background: var(--ring);
}

.filter-btn.active {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border-color: transparent;
}

.file-details {
  background: var(--bg);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 50px;
  border: 1px solid var(--card-border);
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
}

.detail-label {
  color: var(--subtext);
  font-weight: 500;
}

.detail-value {
  color: var(--text);
  direction: ltr;
  text-align: left;
}

.caption-section {
  margin-bottom: 20px;
}

.caption-section label {
  display: block;
  margin-bottom: 8px;
  color: var(--heading);
  font-weight: 500;
  font-size: 14px;
}

.caption-input {
  width: 100%;
  padding: 10px;
  border: 1px solid var(--card-border);
  border-radius: 6px;
  resize: vertical;
  font-size: 14px;
  background: var(--bg);
  color: var(--text);
}

.caption-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--ring);
}

.char-counter {
  text-align: left;
  font-size: 12px;
  color: var(--subtext);
  margin-top: 4px;
}

.size-warning {
  background: rgba(249, 115, 22, 0.1);
  border: 1px solid rgba(249, 115, 22, 0.3);
  color: var(--orange-1);
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 13px;
}

.recent-files h4 {
  margin: 0 0 10px 0;
  font-size: 15px;
  color: var(--heading);
}

.recent-files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.recent-file-item {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.recent-file-item:hover {
  border-color: var(--primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px var(--shadow-1);
}

.recent-file-icon {
  font-size: 24px;
  margin-bottom: 5px;
}

.recent-file-icon.image { color: var(--green); }
.recent-file-icon.pdf { color: var(--orange-1); }
.recent-file-icon.document { color: var(--primary); }
.recent-file-icon.video { color: var(--gradient-2); }
.recent-file-icon.audio { color: var(--orange-2); }
.recent-file-icon.other { color: var(--muted); }

.recent-file-info {
  font-size: 11px;
}

.recent-file-name {
  font-weight: 500;
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  direction: rtl;
  color: var(--text);
}

.recent-file-size {
  color: var(--subtext);
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  background: var(--bg);
  border-top: 1px solid var(--card-border);
}

.cancel-btn, .send-btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.cancel-btn {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  color: var(--subtext);
}

.cancel-btn:hover:not(:disabled) {
  background: var(--hover);
  border-color: var(--muted);
}

.send-btn {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  min-width: 100px;
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-2);
}

.send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.send-btn.loading {
  position: relative;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

.upload-status {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--card-bg);
  padding: 10px 20px;
  border-radius: 25px;
  box-shadow: 0 4px 12px var(--shadow-2);
  display: flex;
  align-items: center;
  gap: 8px;
  animation: slideUp 0.3s ease-out;
  border: 1px solid var(--card-border);
}

.upload-status.success {
  background: var(--green);
  color: white;
  border: none;
}

.upload-status.error {
  background: var(--orange-1);
  color: white;
  border: none;
}

.upload-status.uploading {
  background: var(--primary);
  color: white;
  border: none;
}

.status-icon {
  font-size: 16px;
}

.status-text {
  font-size: 14px;
}

/* Animations */
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes modal-fade {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Responsive styles */
@media (max-width: 768px) {
  .file-upload-modal {
    width: 95%;
    max-height: 85vh;
  }
  
  .file-preview {
    flex-direction: column;
    text-align: center;
  }
  
  .file-info {
    text-align: center;
  }
  
  .file-type-filters {
    overflow-x: auto;
    padding-bottom: 5px;
    flex-wrap: nowrap;
  }
  
  .filter-btn {
    white-space: nowrap;
  }
  
  .recent-files-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .modal-content {
    padding: 15px;
  }
  
  .file-drop-zone {
    padding: 30px 15px;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .cancel-btn, .send-btn {
    width: 100%;
  }
}
</style>